import { graphql } from "gatsby";
import Link from "gatsby-link";
import { get, kebabCase } from "lodash";
import React, { Fragment, useEffect } from "react";
import styled from "styled-components";

import { EVENTS } from "@upsolve/shared";
import { $TSFixMe } from "@upsolve/shared/dist/types";
import { H5, HR, P, Small, theme } from "@upsolve/ui";
import { Ampify } from "../components/AMP/AmpContext";
import CTASideBySideOfferings from "../components/CTA/CTASideBySideOfferings";
import { BankruptcyHeroCTA } from "../components/CTA/HeroCTASets";
import { CTATarget } from "../components/CTA/getCTAData";
import checkForInlineCta from "../components/Contentful/checkForInlineCta";
import getCitationsFromRichText from "../components/Contentful/getCitationsFromRichText";
import getHeadersFromRichText from "../components/Contentful/getHeadersFromRichText";
import getWordCountFromRichText from "../components/Contentful/getWordCountFromRichText";
import richTextNodeToComponent from "../components/Contentful/richTextNodeToComponent";
import richTextNodesInSections from "../components/Contentful/richTextNodesInSections";
import Article from "../components/Layout/Article.div";
import AuthorBio from "../components/Layout/AuthorBio";
import AuthorsBlock from "../components/Layout/AuthorsBlock";
import CitationsList from "../components/Layout/CitationsList";
import ContentSectionFooter from "../components/Layout/ContentSectionFooter";
import Footer from "../components/Layout/Footer";
import Header from "../components/Layout/Header";
import LazyMountOnce from "../components/Layout/LazyMountOnce";
import TableOfContents from "../components/Layout/TableOfContents";
import TopLevelLayout from "../components/Layout/TopLevelLayout";
import { fixHeaderScrollOffset } from "../components/Layout/fixHeaderScrollOffset";
import { learnTagsSortedByPriority } from "../components/Learn/learnTagPriority";
import VideoInContext from "../components/Media/VideoInContext";
import RelatedArticles from "../components/Programmatic/RelatedArticles";
import UpsolveTextBio from "../components/ProofBuilders/UpsolveTextBio";
import SEO from "../components/SEO/SEO";
import * as CONTENT_CREATION_METHODS from "../components/analytics/contentCreationMethods";
import * as CONTENT_GROUPS from "../components/analytics/contentGroups";
import { track } from "../components/analytics/track";
import withScrollWatcher from "../components/analytics/withScrollWatcher";
import withGeoLocation from "../components/maps/withGeoLocation";

const contentGroup = CONTENT_GROUPS.LEARN_ARTICLE;
const templateProps = {
  pageComponentName: "LearnArticle",
  pageComponentVersion: "4",
};

const debtAdvisorSlugs = new Set([
  "debt-collector-scams",
  "mortgage-sold",
  "what-to-do-about-time-barred-debt",
  "going-without-homeowners-insurance",
  "second-mortgage-foreclosure",
]);

const getArticleTarget = (slug: string, tags: { value: string }[]) => {
  if (debtAdvisorSlugs.has(slug)) {
    return CTATarget.DEBT_TRIAGE;
  }
  if (tags.find((tag) => tag.value === learnTagsSortedByPriority.STUDENT_LOANS)) {
    return CTATarget.STUDENT_LOANS;
  }
  return CTATarget.SCREENER;
};

/**
 * LearnArticle
 * - v0: Where we began.
 * - v1: Added geolocation region to persistent cta text
 * - v2: Layout adjustments - condensed top, collapsed TOC, hide CTAs based on screen width
 * - v3: ContentSectionFooter removed, share button at top added, more intuitive Table of Contents
 * - v4: Added mobile CTA, moved authors block to after sidebar
 * - v5: Removed TopOfFunnelAIChat integration, removed debt advisor
 *
 * @component
 * @version 4
 */
type LearnArticleProps = {
  data: { article: $TSFixMe; relatedArticles: { nodes: $TSFixMe } };
  geoLocation: { regionName: string };
  pageContext: { amp: boolean; slug: string };
  scrollWatcher: $TSFixMe;
};

const LearnArticle = (props: LearnArticleProps): React.ReactElement => {
  const { amp, slug } = props.pageContext;
  const { geoLocation } = props;
  const ctaText = `File Bankruptcy${
    geoLocation && geoLocation.regionName ? ` in ${geoLocation.regionName}` : " for Free"
  }`;
  const contentNode = props.data.article;

  const articleTags = contentNode.tags?.filter((tag: { hasDirectoryPage: boolean }) => tag.hasDirectoryPage) || [];
  const wordCount = getWordCountFromRichText(get(props, "data.article.content.json", []));

  const trackingPageContext = {
    contentCreationMethod: CONTENT_CREATION_METHODS.WRITTEN,
    contentGroup,
    contentLocalized: get(props, "data.article.locality") != null,
    publishedAt: get(props, "data.article.publishedAt"),
    slug,
    tags: articleTags.map((tag: { value: string }) => ({ tag: tag.value })),
    updatedAt: get(props, "data.article.updatedAt"),
    wordCount,
    ...templateProps,
  };
  // Comp
  const children = (
    <TopLevelLayout showPersistentCta={false} persistentCtaText={ctaText} trackingPageContext={trackingPageContext}>
      <LearnArticleContents learnArticleProps={props} trackingPageContext={trackingPageContext} />
    </TopLevelLayout>
  );

  return amp === true ? <Ampify>{children}</Ampify> : children;
};

const ArticleTitle = styled.h1`
  color: ${theme.colors.black[300]};
  font-size: 52px;
  font-weight: 500;
  line-height: 58px;
  letter-spacing: -0.5px;
  @media screen and (max-width: 45em) {
    font-size: 36px;
    line-height: 120%;
  }
`;

const ArticleNav = styled.nav`
  @media (max-width: 720px) {
    padding-left: 20px;
  }
`;
const LearnArticleContents = ({
  learnArticleProps,
  trackingPageContext,
}: {
  learnArticleProps: LearnArticleProps;
  trackingPageContext: any;
}) => {
  const { pageContext } = learnArticleProps;
  const { slug } = pageContext;

  const contentNode = learnArticleProps.data.article;
  const seoNode = contentNode.seo;

  const content = get(contentNode, "content.json");
  const sectionHeaders = getHeadersFromRichText(content);
  const citations = getCitationsFromRichText(content);
  const { video } = contentNode;
  const articleTags = contentNode.tags?.filter((tag: { hasDirectoryPage: boolean }) => tag.hasDirectoryPage) || [];
  const hasInlineCta = checkForInlineCta(content.json);
  const authors = (contentNode.authors || [contentNode.author]).filter(
    (author: { name: string | null }) => author && author.name != null
  );
  const wordCount = getWordCountFromRichText(get(learnArticleProps, "data.article.content.json", []));
  const relatedArticles = learnArticleProps.data.relatedArticles.nodes;

  const ctaTarget = getArticleTarget(slug, articleTags);

  // On Mount
  useEffect(() => {
    track(EVENTS.PAGE_VIEW, {
      pageContext: {
        ...trackingPageContext,
        wordCount: wordCount || undefined,
      },
    });
  }, []);

  useEffect(() => {
    fixHeaderScrollOffset();
  }, []);

  const heroCTA = <BankruptcyHeroCTA trackingPageContext={trackingPageContext} ctaTarget={ctaTarget} />;

  // TODO: convert these to react components
  const renderRichTextNodes = (section: any, context: any) =>
    Array.prototype.map.call(section, (c, i) => richTextNodeToComponent(c, i, null, context));

  // Helper functions that could be extracted
  const renderContentSection = (
    section: any,
    sectionIndex: number,
    { hasInlineCta, citations, contentGroup, trackingPageContext, ctaTarget }: any
  ) => {
    const shouldShowMobileCta = !hasInlineCta && sectionIndex === 1;
    // todo: we might want cta before article on single-section article.

    return (
      <Fragment key={sectionIndex}>
        <section>{renderRichTextNodes(section, { contentGroup, citations, trackingPageContext })}</section>
        {shouldShowMobileCta && (
          <div className="mobile-cta-wrapper">
            <aside className="article__sidebar mobile-only">
              <div className="sidebar-hero__cta">{heroCTA}</div>
            </aside>
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <>
      <SEO
        seoNode={seoNode}
        contentNode={{
          ...contentNode,
          citations,
          seoImage: contentNode.seoImage || contentNode.coverImage,
          video,
        }}
      />
      <Header trackingPageContext={trackingPageContext} ctaTarget={ctaTarget} shouldShowNavPopUp={true} />
      <Article>
        <ArticleNav>
          <div>
            <a aria-busy="false" href="/learn/">
              ← Learning Center
            </a>
          </div>
          {articleTags.length > 0 && (
            <div style={{ display: "flex", gap: "8px" }}>
              <div>Tags: </div>
              {articleTags.map((tag: { value: string; label: string }, index: number) => (
                <div key={tag.value} style={{ display: "flex", gap: "8px" }}>
                  {index > 0 ? <div>|</div> : <></>}
                  <a aria-busy="false" href={`/learn/category/${kebabCase(tag.value)}/`}>
                    {tag.label}
                  </a>
                </div>
              ))}
            </div>
          )}
        </ArticleNav>
        <div className="article__body">
          <div className="article__content">
            <div className="content-hero">
              <header className="content-hero__header">
                <ArticleTitle>{contentNode.title}</ArticleTitle>
                <UpsolveTextBio
                  wordCount={wordCount || undefined}
                  ctaTarget={ctaTarget}
                  trackingPageContext={trackingPageContext}
                />
                <HR />
              </header>
            </div>
            <div className="content-body">
              <article className="content-body__container">
                {get(contentNode, "articleExcerpt.articleExcerpt") != null && (
                  <div className="content-body__lead">
                    <div className="summary">
                      <b>In a Nutshell</b>
                      <P>{get(contentNode, "articleExcerpt.articleExcerpt")}</P>
                      {/* <ArticleSummaryMessageBox handleSendMessage={handleSendMessageFromArticleSummary} /> */}
                    </div>
                  </div>
                )}
                <AuthorsBlock
                  authors={authors}
                  createdAt={contentNode.createdAt}
                  updatedAt={contentNode.updatedAt}
                  reviewedAt={undefined}
                />
                <HR />
                {sectionHeaders.length > 1 && <TableOfContents sectionHeaders={sectionHeaders} />}
                {richTextNodesInSections(content).length === 1
                  ? renderContentSection(richTextNodesInSections(content)[0], 1, {
                      hasInlineCta,
                      content,
                      citations,
                      contentGroup,
                      trackingPageContext,
                      ctaTarget,
                    })
                  : richTextNodesInSections(content).map((section, sectionIndex) =>
                      renderContentSection(section, sectionIndex, {
                        hasInlineCta,
                        content,
                        citations,
                        contentGroup,
                        trackingPageContext,
                        ctaTarget,
                      })
                    )}
                {video != null && (
                  <section className="content-video">
                    <VideoInContext video={video} />
                  </section>
                )}
                <HR />
                {citations.length > 0 && (
                  <Fragment>
                    <H5 as="h2">Sources:</H5>
                    <CitationsList citations={citations} />
                  </Fragment>
                )}
                <ContentSectionFooter />
                <HR />
                {authors.length > 0 && (
                  <div className="author-bios">
                    <div className="written-by">
                      <Small>Written By:</Small>
                    </div>
                    {Array.prototype.map.call(authors, (author) => (
                      <AuthorBio key={author.name} author={author} />
                    ))}
                    <div className="team">
                      <Link to="/team/">Read About the Upsolve Team</Link>
                    </div>
                  </div>
                )}
              </article>
              {/* Desktop CTA - shows only on desktop */}
              <aside className="article__sidebar desktop-only">
                <div className="sidebar-hero__cta">{heroCTA}</div>
              </aside>
            </div>
          </div>
        </div>
      </Article>
      <RelatedArticles articles={relatedArticles} />
      <CTASideBySideOfferings trackingPageContext={trackingPageContext} ctaTarget={ctaTarget} />
      <LazyMountOnce>
        <Footer showArticles />
      </LazyMountOnce>
    </>
  );
};

//TODO: @Jeff, could not figure out why callToActionTag is returning null, and cannot figure out how to test this query on graphiQL. using a constant list for AJM articles for now.
export const pageQuery = graphql`
  query LearnArticleBySlug($slug: String!, $relatedArticleIds: [String]) {
    article: contentfulLearnArticle(slug: { eq: $slug }) {
      slug
      title
      publishedAt
      tags {
        value
        label
        hasDirectoryPage
      }
      coverImage {
        image {
          file {
            url
          }
          fluid(maxWidth: 960) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
      }
      articleExcerpt {
        articleExcerpt
      }
      video {
        ...VideoNode
      }
      author {
        ...AuthorNode
      }
      authors {
        ...AuthorNode
      }
      content {
        json
      }
      seo {
        ...SeoNode
      }
      createdAt
      updatedAt
    }
    relatedArticles: allContentfulLearnArticle(filter: { id: { in: $relatedArticleIds }, slug: { ne: $slug } }) {
      nodes {
        createdAt
        slug
        title
        authors {
          name
        }
      }
    }
  }
`;

export default withScrollWatcher(withGeoLocation(LearnArticle));
